import { NgClass, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { ImageModule } from 'primeng/image';
import { TooltipModule } from 'primeng/tooltip';
import { forkJoin, Observable } from 'rxjs';
import { AzureBlobService } from 'src/app/shared/services/azure-blob.service';
import { ChatService } from 'src/app/shared/services/chat.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import {
  AppConstant,
  Translation,
  TranslationData,
} from 'src/app/shared/utilities/app.constant';
import { AppHelper } from 'src/app/shared/utilities/app.helper';
import { AvatarComponent } from '../../../../modules/avatar/avatar/avatar.component';
import { UtileSafePipe } from '../../../../shared/pipes/utile/utele-safe.pipe';
import { ChatMessageTimeComponent } from '../chat-message-time/chat-message-time.component';
import { SkeletonModule } from 'primeng/skeleton';

const TALK_TO_AGENT_TRANSLATIONS: Translation = {
  en: 'Talk to an agent',
  es: 'Habla con un agente',
  pt: 'Fale com um agente',
};

@Component({
  selector: 'app-chat-message-text',
  templateUrl: './chat-message-text.component.html',
  styleUrls: ['./chat-message-text.component.scss'],
  imports: [
    NgIf,
    NgClass,
    AvatarComponent,
    ChatMessageTimeComponent,
    NgFor,
    TooltipModule,
    ImageModule,
    UtileSafePipe,
    SvgIconComponent,
    SkeletonModule,
  ],
})
export class ChatMessageTextComponent implements OnInit, OnChanges {
  @Input() message: AppConstant.Message;
  @Input() previousMessage: AppConstant.Message | undefined;

  @Input() isMine: boolean;

  @Input() isTyping: boolean = false;
  @Input() template: 'CONTENT' | 'LOADING' = 'CONTENT';
  @Input() translationData: TranslationData;

  listBlobImg: any[] = [];
  listFileAnother: any[] = [];
  isLoadingAttachment: boolean = false;
  isShowingTranslation: boolean = false;
  isTranslating: boolean = false;
  talkToAgentTranslations: Translation = TALK_TO_AGENT_TRANSLATIONS;

  constructor(
    private _blobService: AzureBlobService,
    private _notificationService: NotificationService,
    private _chatService: ChatService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.isShowingTranslation = this.translationData?.hasTranslation;
    if (this.message.attachments?.length) {
      let url: Observable<any>[] = [];
      this.listBlobImg = [];
      this.listFileAnother = [];
      let listNameImgTemp: string[] = [];

      this.message.attachments.forEach((file: any) => {
        const fileType = (file.name.split('.')[1] as string).toLowerCase();
        const typeAllow = ['jpg', 'jpeg', 'png'];

        if (typeAllow.includes(fileType)) {
          url.push(this._blobService.getFileByUrl(file.url));
          listNameImgTemp.push(file.name);
          this.isLoadingAttachment = true;
        } else
          this.listFileAnother.push({
            type: fileType,
            name: AppHelper.UtileFunctions.decodeFileNameFromAzureUrl(
              file.name
            ),
            url: file.url,
          });
      });

      forkJoin(url).subscribe({
        next: (response: any[]) => {
          response.forEach((blob: any, index: number) => {
            this.listBlobImg.push({
              url: URL.createObjectURL(blob),
              name: listNameImgTemp[index],
            });
          });
          this.isLoadingAttachment = false;
          this.cdr.markForCheck(); 
        },
        error: () => {
          this.isLoadingAttachment = false;
          this.cdr.markForCheck(); 
        },
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['translationData']) {
      const hasTranslation = !!this.translationData?.translatedContent;
      if (hasTranslation) {
        this.isTranslating = false;
      }
      if (this.translationData?.hasTranslation && !this.isMine) {
        this.isShowingTranslation = true;
      }
    }
  }

  onClickTalkToAgent() {
    this._chatService
      .updateInteractionMesssage(
        this.message.id,
        this.message,
        this.talkToAgentTranslations[
          this.translationData.userPreferredLanguage || 'en'
        ],
        AppConstant.ActionButton.END_USER__TALK_TO_AN_AGENT
      )
      .subscribe(() => {});
  }

  openFile(file: any) {
    if (file?.url) {
      this._blobService.getFileByUrl(file.url).subscribe({
        next: (blob: Blob) => {
          AppHelper.UtileFunctions.clickDownloadDocumentWithName(
            blob,
            file.name
          );
        },
        error: error => {
          console.error('Error: ' + error);
          this._notificationService.setMessage({
            type: AppConstant.MESSAGE_TYPE.WARNING,
            header: 'Loading Document',
            content: 'This document is no longer available, please try again',
          });
        },
      });
    } else {
      this._notificationService.setMessage({
        type: AppConstant.MESSAGE_TYPE.WARNING,
        header: 'Read Document',
        content: "Can't find the document in the database",
      });
    }
  }

  onClickTranslation() {
    if (this.translationData?.translatedContent) {
      this.isShowingTranslation = !this.isShowingTranslation;
    } else {
      // Call API to translate the message
      if (this.translationData?.userPreferredLanguage) {
        this.isTranslating = true;
        this._chatService
          .translateMessage(
            this.message.id,
            this.translationData.userPreferredLanguage
          )
          .subscribe({
            next: () => {
              this.isTranslating = false;
              this.isShowingTranslation = true;
            },
            error: err => {
              console.error('Translation failed:', err);
              this.isTranslating = false;
            },
          });
      }
    }
  }

  downloadImage(url: any, fileName: string) {
    fetch(url)
      .then(response => response.blob())
      .then((blob: any) => {
        AppHelper.UtileFunctions.clickDownloadDocumentWithName(blob, fileName);
      });
  }
}
